'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { allDaysContent, week1Rituals } from './daysData';
import { saveDailyResponse } from '@/lib/services/dailyResponseService';
import {
  fetchCustomScenarios,
  insertCustomScenario,
  CustomScenario,
} from '@/lib/services/customScenarioService';

interface DayActivity {
  day: number;
  title: string;
  category: string;
  description: string;
  duration: string;
}

interface UserProgress {
  email: string;
  fullName: string;
  completedDays: number[];
  currentDay: number;
  journeyStartDate: string;
}

interface Week1DayContent {
  objective: string;
  howTo: string;
  sections: {
    title: string;
    icon: string;
    reflection: string[];
    example: string;
    emotionalResponse: string;
    insight: string;
    action: string;
  }[];
}

// ─── EAT (UTC+3) Utilities ────────────────────────────────────────────────────

/** Returns current date/time in Dar es Salaam (EAT = UTC+3) */
function getEATNow(): Date {
  const now = new Date();
  // EAT offset: UTC+3 = 3*60 minutes
  const eatOffsetMs = 3 * 60 * 60 * 1000;
  return new Date(now.getTime() + eatOffsetMs - now.getTimezoneOffset() * 60 * 1000);
}

/** Returns today's date string in EAT as YYYY-MM-DD */
function getEATDateString(date?: Date): string {
  const d = date || getEATNow();
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Returns how many days have elapsed since startDate (EAT), 0-indexed */
function daysSinceStart(startDateStr: string): number {
  const eatNow = getEATNow();
  const todayStr = getEATDateString(eatNow);
  const start = new Date(startDateStr + 'T00:00:00Z');
  const today = new Date(todayStr + 'T00:00:00Z');
  const diff = Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

/** Returns ms until next midnight EAT */
function msUntilEATMidnight(): number {
  const eatNow = getEATNow();
  const eatOffsetMs = 3 * 60 * 60 * 1000;
  // Next midnight in EAT
  const nextMidnightEAT = new Date(
    Date.UTC(eatNow.getUTCFullYear(), eatNow.getUTCMonth(), eatNow.getUTCDate() + 1, 0, 0, 0)
  );
  const nowUTC = new Date(new Date().getTime());
  return nextMidnightEAT.getTime() - eatOffsetMs - nowUTC.getTime() + nowUTC.getTimezoneOffset() * 60 * 1000;
}

/** Returns true if current EAT time is midnight (00:00–00:05 window) */
function isEATMidnightWindow(): boolean {
  const eatNow = getEATNow();
  const hours = eatNow.getUTCHours();
  const minutes = eatNow.getUTCMinutes();
  return hours === 0 && minutes < 5;
}

/** Format EAT time as HH:MM:SS */
function formatEATTime(date: Date): string {
  const h = String(date.getUTCHours()).padStart(2, '0');
  const m = String(date.getUTCMinutes()).padStart(2, '0');
  const s = String(date.getUTCSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

// ─── Night Meditation Ritual ──────────────────────────────────────────────────

const nightMeditationRitual = {
  title: 'Night Meditation: No Enemy Living',
  subtitle: 'Optional · For the Deeply Committed',
  description:
    'A sacred end-of-day practice for those fully committed to the No Enemy path. This meditation guides you through releasing the day\'s tensions, extending forgiveness to all who crossed your path, and resting in the peace of having no enemy in your heart.',
  duration: '15–20 min',
  steps: [
    {
      step: 1,
      title: 'Settle & Breathe',
      instruction:
        'Find a quiet place. Sit or lie down comfortably. Close your eyes. Take 5 slow, deep breaths — inhale peace, exhale tension. Let the day begin to dissolve.',
    },
    {
      step: 2,
      title: 'Review the Day with Compassion',
      instruction:
        'Gently replay your day. Who did you encounter? Where did tension arise? Where did you feel resistance or judgment? Observe without self-criticism — you are simply witnessing.',
    },
    {
      step: 3,
      title: 'Release Each Encounter',
      instruction:
        'For each person or situation that brought friction today, silently say: "I release you. I hold no enmity toward you. You are free, and so am I." Breathe after each release.',
    },
    {
      step: 4,
      title: 'The No Enemy Affirmation',
      instruction:
        'Place your hand on your heart. Say aloud or silently: "As I close this day, I carry no enemy in my heart. Every person I encountered today is a fellow human being on their own journey. I choose peace. I choose no enemy."',
    },
    {
      step: 5,
      title: 'Gratitude & Rest',
      instruction:
        'Name three things from today you are grateful for — however small. Then rest in the silence of a heart at peace. You have done the work. Sleep in peace.',
    },
  ],
};

// ─── Week 1–4 Detailed Content ────────────────────────────────────────────────

const week1DetailedContent: Record<number, Week1DayContent> = {
  1: {
    objective: 'Identify your dominant emotions and their immediate triggers within different life situations. This is the first step toward forgiveness, healing, unity, and living in peace.',
    howTo: 'For each life scenario below, read the reflection questions, consider the example, and identify your own emotional response. Then write your personal action statement in a journal.',
    sections: [
      {
        title: 'Team Work (Recognition)',
        icon: 'UsersIcon',
        reflection: ['What emotions dominate my day?', 'When do I feel most unappreciated at work?'],
        example: 'Feeling irritated during team meetings when your contributions are overlooked',
        emotionalResponse: 'Irritation, frustration, resentment',
        insight: 'Irritation is often linked to feeling unheard or undervalued. Recognizing this helps you separate the emotion from the person.',
        action: 'Write in your journal: "I feel frustrated when my ideas are ignored because I value being heard and respected."',
      },
      {
        title: 'Love Affairs (Romantic Relationship)',
        icon: 'HeartIcon',
        reflection: ['What emotions do I feel toward my partner today?', 'What situation recently disturbed my emotional balance?', 'Am I reacting to the present or to a past wound?'],
        example: 'Partner delayed responding to messages or appeared distant and unavailable',
        emotionalResponse: 'Anxiety, insecurity, overthinking, fear of abandonment',
        insight: 'Fear of rejection and the need for reassurance are deeply human. Naming this emotion reduces its power over your reactions.',
        action: 'Write in your journal: "I feel anxious when my partner is distant because I value closeness and security. This is about my need, not their intent."',
      },
      {
        title: 'Husband & Wife (Marriage)',
        icon: 'HomeIcon',
        reflection: ['What recurring emotional tension exists in my marriage?', 'When do I feel most misunderstood by my spouse?', 'What do I wish my partner understood about me?'],
        example: 'Spouse dismisses your opinion during household decision-making or financial planning',
        emotionalResponse: 'Frustration, feeling disrespected, suppressed anger, withdrawal',
        insight: 'The emotional need for recognition and mutual respect is foundational in marriage. Unmet, it builds invisible walls between partners.',
        action: 'Write in your journal: "I feel frustrated when my input is overlooked because I value mutual respect and inclusion in our shared life."',
      },
      {
        title: 'Bad Boss (Workplace Conflict)',
        icon: 'BriefcaseIcon',
        reflection: ['What emotions arise when I think about my supervisor?', 'What specific behavior triggers these emotions?', 'How does this affect my performance and self-worth?'],
        example: 'Boss criticizes your work harshly in front of colleagues during a team meeting',
        emotionalResponse: 'Embarrassment, anger, demotivation, self-doubt',
        insight: 'Sensitivity to public criticism reflects a desire for professional dignity. This is not weakness — it is a healthy need for respectful leadership.',
        action: 'Write in your journal: "I feel demotivated when criticized publicly because I value respect and constructive feedback. I deserve to be corrected with dignity."',
      },
      {
        title: 'No Employment (Unemployment)',
        icon: 'ExclamationCircleIcon',
        reflection: ['What emotions dominate my current situation?', 'What thoughts repeatedly come to mind about my future?', 'How am I treating myself during this difficult season?'],
        example: 'Experiencing repeated job rejections or prolonged lack of employment opportunities',
        emotionalResponse: 'Stress, self-doubt, hopelessness, shame, isolation',
        insight: 'Fear of uncertainty and a perceived lack of progress are natural responses to unemployment. Your worth is not defined by your employment status.',
        action: 'Write in your journal: "I feel anxious about my future due to uncertainty, not because I lack ability. I am capable, and this season is temporary."',
      },
    ],
  },
  2: {
    objective: 'Identify the relationships in your life where hostility, resentment, or unresolved conflict exists. Naming your "enemies" is not about blame — it is about honest self-awareness.',
    howTo: 'Reflect on each life area below. For each scenario, consider who or what triggers negative emotions in you. Write down names or situations without judgment — this is a private, safe exercise.',
    sections: [
      {
        title: 'Family Tensions',
        icon: 'HomeIcon',
        reflection: ['Who in my family do I feel unresolved tension with?', 'What incident created this distance?', 'Have I ever expressed how I truly feel?'],
        example: 'A sibling who received more attention growing up, or a parent whose words still sting years later',
        emotionalResponse: 'Resentment, jealousy, longing, unspoken grief',
        insight: 'Family wounds are often the deepest because they involve people we expected to love us unconditionally. Naming the pain is the first act of healing.',
        action: 'Write in your journal: "The family relationship that holds the most unresolved pain for me is ___. The emotion I carry is ___."',
      },
      {
        title: 'Workplace Rivals',
        icon: 'BriefcaseIcon',
        reflection: ['Is there a colleague I avoid or feel competitive with?', 'What did they do that I have not forgiven?', 'How much mental energy do I spend on this person?'],
        example: 'A coworker who took credit for your idea or was promoted over you unfairly',
        emotionalResponse: 'Bitterness, envy, distrust, professional insecurity',
        insight: 'Workplace hostility drains your creativity and focus. Recognizing who occupies your mental space helps you reclaim that energy.',
        action: 'Write in your journal: "At work, I feel hostility toward ___ because ___. This costs me ___ in energy and peace."',
      },
      {
        title: 'Romantic Wounds',
        icon: 'HeartIcon',
        reflection: ['Is there a past or present partner I still carry anger toward?', 'What did they do that felt like a betrayal?', 'Am I projecting past wounds onto current relationships?'],
        example: 'An ex-partner who was unfaithful, or a current partner whose behavior reopens old wounds',
        emotionalResponse: 'Betrayal, anger, grief, guardedness',
        insight: 'Romantic wounds often shape how we love in the future. Identifying them prevents us from punishing new partners for old pain.',
        action: 'Write in your journal: "The romantic wound I still carry is ___. The person involved is ___. What I need to release is ___."',
      },
      {
        title: 'Social & Community Conflicts',
        icon: 'UsersIcon',
        reflection: ['Is there someone in my community or social circle I avoid?', 'What happened between us?', 'How has this affected my sense of belonging?'],
        example: 'A neighbor dispute, a falling out with a friend group, or a community leader who wronged you',
        emotionalResponse: 'Exclusion, anger, social anxiety, distrust of community',
        insight: 'Social conflicts can make us feel unsafe in spaces meant for connection. Naming them helps us decide what to repair and what to release.',
        action: 'Write in your journal: "In my community, I feel disconnected from ___ because ___. What I wish had happened differently is ___."',
      },
      {
        title: 'Self as Enemy',
        icon: 'ExclamationCircleIcon',
        reflection: ['In what ways am I my own worst enemy?', 'What self-critical thoughts do I repeat most often?', 'What past mistake do I refuse to forgive myself for?'],
        example: 'Constantly replaying a failure, sabotaging opportunities out of fear, or speaking harshly to yourself',
        emotionalResponse: 'Shame, guilt, self-loathing, perfectionism',
        insight: 'The most persistent enemy is often the inner critic. Forgiving yourself is not weakness — it is the foundation of all other forgiveness.',
        action: 'Write in your journal: "The way I am my own enemy is ___. The self-criticism I most need to release is ___."',
      },
    ],
  },
  3: {
    objective: 'Examine the real cost of carrying hostility, grudges, and resentment. Understanding what unforgiveness costs you — physically, emotionally, and relationally — creates the motivation to choose peace.',
    howTo: 'For each life area, honestly assess how holding onto hostility has affected you. This is not about blame — it is about recognizing the price you pay when you remain at war.',
    sections: [
      {
        title: 'Physical Health Cost',
        icon: 'HeartIcon',
        reflection: ['Do I notice physical tension when I think about certain people?', 'Have I experienced stress-related health issues?', 'Where does my body hold unresolved anger?'],
        example: 'Chronic headaches, tight shoulders, disrupted sleep, or elevated blood pressure linked to ongoing conflict',
        emotionalResponse: 'Physical tension, fatigue, chronic stress, somatic pain',
        insight: 'Research shows that sustained hostility elevates cortisol and adrenaline, weakening the immune system. Your body keeps the score of every unresolved conflict.',
        action: 'Write in your journal: "The physical symptoms I notice when I think about my unresolved conflicts are ___. My body is telling me ___."',
      },
      {
        title: 'Mental & Emotional Cost',
        icon: 'LightBulbIcon',
        reflection: ['How much mental space do my grudges occupy?', 'Do I replay past hurts repeatedly?', 'How does resentment affect my mood and daily focus?'],
        example: 'Spending hours mentally rehearsing arguments, losing sleep over past injustices, or feeling emotionally exhausted',
        emotionalResponse: 'Mental fatigue, rumination, anxiety, emotional numbness',
        insight: 'Every minute spent replaying a grievance is a minute stolen from your present joy. Resentment is a mental prison with a door only you can open.',
        action: 'Write in your journal: "I estimate I spend ___ hours per week thinking about my unresolved conflicts. This mental energy could instead be used for ___."',
      },
      {
        title: 'Relationship Cost',
        icon: 'UsersIcon',
        reflection: ['Which relationships have I damaged or lost due to unresolved hostility?', 'Who have I pushed away because of my guarded heart?', 'What connections am I missing out on?'],
        example: 'Avoiding family gatherings, losing friendships due to sides taken, or being emotionally unavailable to loved ones',
        emotionalResponse: 'Isolation, loneliness, relational poverty, missed connection',
        insight: 'Hostility creates invisible walls. While protecting you from one person, it often keeps out everyone else. The cost is relational poverty.',
        action: 'Write in your journal: "The relationships most affected by my unresolved hostility are ___. What I have lost or missed because of this is ___."',
      },
      {
        title: 'Professional Cost',
        icon: 'BriefcaseIcon',
        reflection: ['Has workplace hostility affected my performance or reputation?', 'Have I missed opportunities due to conflict with colleagues?', 'How does carrying resentment affect my professional presence?'],
        example: 'Avoiding collaboration with a rival, underperforming due to a toxic environment, or being passed over due to perceived negativity',
        emotionalResponse: 'Professional stagnation, disengagement, reduced creativity',
        insight: 'Unresolved workplace conflict is one of the leading causes of burnout and career stagnation. Peace at work is a professional asset.',
        action: 'Write in your journal: "The professional cost of my unresolved workplace conflicts has been ___. If I released this, I could ___."',
      },
      {
        title: 'Spiritual & Purpose Cost',
        icon: 'SparklesIcon',
        reflection: ['Does carrying hostility align with my deepest values?', 'How does resentment affect my sense of purpose?', 'What kind of person do I want to be?'],
        example: 'Feeling disconnected from your values, losing motivation for meaningful work, or feeling spiritually hollow',
        emotionalResponse: 'Spiritual disconnection, loss of purpose, moral dissonance',
        insight: 'Most people\'s deepest values include love, peace, and compassion. Hostility creates a gap between who we are and who we want to be. Closing that gap is the work of forgiveness.',
        action: 'Write in your journal: "The person I want to be is ___. The way hostility prevents me from being that person is ___. I choose to begin closing this gap."',
      },
    ],
  },
  4: {
    objective: 'Develop compassion — for yourself and for others, including those who have hurt you. Compassion is not condoning harm; it is recognizing shared humanity as the foundation of healing.',
    howTo: 'Move through each meditation focus slowly. Read the reflection questions, sit with the example, and allow yourself to feel without judgment. This is a practice — not a performance.',
    sections: [
      {
        title: 'Self-Compassion Foundation',
        icon: 'HeartIcon',
        reflection: ['How do I speak to myself when I make mistakes?', 'Would I speak this way to a friend?', 'What does my inner voice most need to hear today?'],
        example: 'Noticing the harsh inner critic that emerges after a mistake and consciously choosing a gentler response',
        emotionalResponse: 'Shame, self-criticism, perfectionism — softening into acceptance',
        insight: 'Self-compassion is the prerequisite for compassion toward others. You cannot give what you have not received — even from yourself.',
        action: 'Place your hand on your heart. Breathe slowly. Say aloud: "I am doing my best. I am worthy of kindness. I forgive myself for my imperfections."',
      },
      {
        title: 'Compassion for Those Who Hurt You',
        icon: 'UsersIcon',
        reflection: ['What pain might the person who hurt me have been carrying?', 'What circumstances shaped them into who they are?', 'Can I hold their humanity alongside my own pain?'],
        example: 'Considering that a parent who was emotionally unavailable may have been carrying their own unhealed trauma',
        emotionalResponse: 'Resistance, then gradual softening, complex grief',
        insight: 'Understanding someone\'s pain does not excuse their behavior. But it frees you from the prison of pure hatred. Compassion is for your liberation, not their absolution.',
        action: 'Write in your journal: "The person I struggle most to have compassion for is ___. One thing I know about their pain or history is ___."',
      },
      {
        title: 'Compassion in Relationships',
        icon: 'HomeIcon',
        reflection: ['When did I last extend genuine compassion to my partner or family?', 'What is my loved one carrying that I may not fully see?', 'How can I respond with curiosity instead of judgment today?'],
        example: 'A partner who seems withdrawn may be overwhelmed with anxiety, not indifferent to you',
        emotionalResponse: 'Frustration softening into curiosity and care',
        insight: 'Most relational conflict is two people in pain, each waiting for the other to show compassion first. Someone has to go first. Let it be you.',
        action: 'Today, ask one person close to you: "How are you really doing?" — and listen without preparing your response.',
      },
      {
        title: 'Compassion in the Workplace',
        icon: 'BriefcaseIcon',
        reflection: ['What pressures might my difficult colleague or boss be under?', 'Have I ever acted poorly under stress?', 'How would I want to be treated in my worst moment?'],
        example: 'A manager who micromanages may be operating from fear of failure, not a desire to control you',
        emotionalResponse: 'Resentment shifting toward understanding',
        insight: 'Workplace compassion does not mean tolerating mistreatment. It means seeing the human behind the behavior — which makes you more effective, not less.',
        action: 'Write in your journal: "One person at work I could extend more compassion to is ___. What I imagine they might be carrying is ___."',
      },
      {
        title: 'Global Compassion Practice',
        icon: 'GlobeAltIcon',
        reflection: ['Can I extend compassion to people I have never met who are suffering?', 'How does global suffering connect to my personal healing?', 'What would the world look like if compassion were the default response?'],
        example: 'Sitting with news of conflict or injustice and choosing to respond with compassion rather than outrage or numbness',
        emotionalResponse: 'Overwhelm, grief, then purposeful compassion',
        insight: 'Personal healing and global peace are not separate. Every act of compassion in your own life contributes to the collective shift toward a world with no enemy.',
        action: 'Close your eyes. Breathe. Silently send compassion to: yourself, someone you love, someone who hurt you, a stranger suffering somewhere in the world.',
      },
    ],
  },
  5: {
    objective: 'Experience the healing power of community. Forgiveness is not only a private journey — it is strengthened and sustained through connection with others on the same path.',
    howTo: 'Explore each connection opportunity below. Choose at least one action to take today. Community is where personal transformation becomes collective transformation.',
    sections: [
      {
        title: 'Finding Your Local Chapter',
        icon: 'MapPinIcon',
        reflection: ['Is there a SIG Day community near me?', 'What would it mean to share this journey with others?', 'What do I fear about joining a community?'],
        example: 'Attending a local SIG Day gathering for the first time and finding others on the same journey',
        emotionalResponse: 'Nervousness, hope, belonging, relief',
        insight: 'The moment you realize you are not alone in your struggle is often the moment healing accelerates. Community is not a luxury — it is a catalyst.',
        action: 'Visit the SIG Day website or community forum today. Find one local event or online group and take one step toward joining.',
      },
      {
        title: 'Online Community Engagement',
        icon: 'GlobeAltIcon',
        reflection: ['How can I contribute to an online community today?', 'What part of my journey might encourage someone else?', 'What do I hope to receive from community connection?'],
        example: 'Sharing a reflection in the community forum that resonates with others and sparks meaningful conversation',
        emotionalResponse: 'Vulnerability, connection, mutual recognition',
        insight: 'Your story — including the pain, the struggle, and the small victories — is a gift to others. Sharing it is both an act of courage and an act of service.',
        action: 'Post one honest reflection in the SIG Day community forum today. It can be a question, an insight, or simply where you are on your journey.',
      },
      {
        title: 'Accountability Partnership',
        icon: 'UsersIcon',
        reflection: ['Is there someone in my life who could join this 30-day journey with me?', 'What would it mean to have someone to check in with daily?', 'Who in my life is also seeking healing?'],
        example: 'Inviting a trusted friend to do the 30-day transformation calendar together and checking in each evening',
        emotionalResponse: 'Shared purpose, motivation, accountability',
        insight: 'Research consistently shows that shared goals are more likely to be achieved. An accountability partner transforms a private practice into a shared commitment.',
        action: 'Reach out to one person today and invite them to join you on this 30-day journey. Share this guide with them.',
      },
      {
        title: 'Community Service Action',
        icon: 'HeartIcon',
        reflection: ['How can I serve my community today, even in a small way?', 'Who near me is in need of support?', 'How does serving others connect to my own healing?'],
        example: 'Volunteering at a local organization, helping a neighbor, or simply offering a kind word to someone struggling',
        emotionalResponse: 'Purpose, connection, joy, expanded perspective',
        insight: 'Service shifts the focus from your own pain to someone else\'s need. This perspective shift is one of the most powerful healing tools available.',
        action: 'Perform one act of community service today — no matter how small. Write about how it made you feel in your journal.',
      },
      {
        title: 'Sharing Your Story',
        icon: 'SparklesIcon',
        reflection: ['What part of my transformation story is ready to be shared?', 'Who might be helped by hearing my experience?', 'What would I want someone earlier on this journey to know?'],
        example: 'Writing a brief testimony about what brought you to this journey and what you hope to find',
        emotionalResponse: 'Courage, vulnerability, purpose',
        insight: 'Your story — including the pain, the struggle, and the small victories — is a gift to others. Sharing it is both an act of courage and an act of service.',
        action: 'Write a 3–5 sentence story about what brought you to this journey. Consider sharing it in the community forum or with your accountability partner.',
      },
    ],
  },
  6: {
    objective: 'Begin the process of releasing resentment through the powerful practice of forgiveness letter writing. This is not about reconciliation — it is about your own freedom.',
    howTo: 'Choose one person or situation from each life area that still carries emotional charge. Write freely and honestly — this letter is for your healing, not for sending.',
    sections: [
      {
        title: 'Understanding Forgiveness Letters',
        icon: 'LightBulbIcon',
        reflection: ['What do I believe forgiveness means?', 'Am I confusing forgiveness with condoning harm?', 'What would it feel like to be free from this resentment?'],
        example: 'Realizing that writing a forgiveness letter is an act of self-liberation, not a gift to the person who hurt you',
        emotionalResponse: 'Resistance, then curiosity, then cautious openness',
        insight: 'Forgiveness is not saying "what you did was okay." It is saying "I refuse to let what you did continue to define my life." It is a gift you give yourself.',
        action: 'Before writing, sit quietly for 2 minutes. Breathe. Set this intention: "I am writing this letter for my own freedom and peace."',
      },
      {
        title: 'Letter to a Family Member',
        icon: 'HomeIcon',
        reflection: ['What did this family member do that still hurts?', 'What did I need from them that I did not receive?', 'What would I want them to understand about my experience?'],
        example: 'Writing to a parent about the emotional neglect experienced in childhood, expressing the pain and the desire to heal',
        emotionalResponse: 'Grief, anger, longing, gradual release',
        insight: 'Family forgiveness letters often unlock the deepest grief — and the deepest healing. Allow yourself to feel everything as you write.',
        action: 'Write your letter beginning with: "Dear ___, I have been carrying something I need to release. What I experienced was... What I needed was... What I am choosing now is..."',
      },
      {
        title: 'Letter to a Colleague or Boss',
        icon: 'BriefcaseIcon',
        reflection: ['What professional wound am I still carrying?', 'How has this person\'s behavior affected my confidence or career?', 'What would I want them to know?'],
        example: 'Writing to a former boss who undermined your work, expressing the impact of their behavior and choosing to release it',
        emotionalResponse: 'Professional anger, wounded pride, gradual empowerment',
        insight: 'Professional wounds often attack our sense of competence and worth. Naming this in a letter reclaims your narrative and your power.',
        action: 'Write: "Dear ___, Your actions affected me in these ways... What I want you to know is... I am choosing to release this so I can move forward with my full potential."',
      },
      {
        title: 'Letter to a Romantic Partner',
        icon: 'HeartIcon',
        reflection: ['What romantic wound am I still carrying?', 'How has this experience shaped how I love?', 'What do I need to release to love freely again?'],
        example: 'Writing to an ex-partner about betrayal, expressing the full depth of the hurt and choosing to release its hold',
        emotionalResponse: 'Deep grief, anger, vulnerability, eventual peace',
        insight: 'Romantic wounds often become the lens through which we see all future relationships. Releasing them through writing frees you to love without the weight of the past.',
        action: 'Write: "Dear ___, What you did changed me in these ways... What I lost was... What I am reclaiming now is... I release you and I release this pain."',
      },
      {
        title: 'Letter to Yourself',
        icon: 'SparklesIcon',
        reflection: ['What have I done that I have not forgiven myself for?', 'How long have I been punishing myself for this?', 'What would I say to a dear friend in this same situation?'],
        example: 'Writing to yourself about a past mistake, a failure, or a choice you deeply regret — and choosing self-forgiveness',
        emotionalResponse: 'Shame, guilt, then compassion, then release',
        insight: 'The letter to yourself is often the hardest and the most transformative. You deserve the same forgiveness you are learning to extend to others.',
        action: 'Write: "Dear [Your Name], I have been carrying guilt about... What I needed to hear then was... What I am choosing to believe about myself now is... I forgive you."',
      },
    ],
  },
  7: {
    objective: 'Consolidate the learning from your first week. Reflection is not looking backward — it is gathering wisdom to carry forward. Celebrate what has shifted, acknowledge what remains difficult, and recommit to the journey.',
    howTo: 'Move through each reflection area honestly. There are no right or wrong answers — only your truth. This is a sacred pause before Week 2 begins.',
    sections: [
      {
        title: 'Emotional Discoveries (Days 1–3)',
        icon: 'SparklesIcon',
        reflection: ['What emotions surprised me this week?', 'Which life area holds the most unresolved pain?', 'What did I learn about myself that I did not know before?'],
        example: 'Discovering that your deepest resentment is not toward an external person but toward yourself for allowing a situation to continue',
        emotionalResponse: 'Surprise, recognition, sometimes grief, often relief',
        insight: 'The first three days of this journey are about honest seeing. What you discovered this week — however uncomfortable — is the raw material of transformation.',
        action: 'Write in your journal: "The most significant emotional discovery I made this week was ___. This surprised me because ___."',
      },
      {
        title: 'Compassion Practice Progress (Day 4)',
        icon: 'HeartIcon',
        reflection: ['How did the compassion meditation feel?', 'Was it easier to extend compassion to yourself or to others?', 'Who was hardest to hold with compassion?'],
        example: 'Finding it easier to feel compassion for a stranger than for a family member who hurt you — and sitting with that honest observation',
        emotionalResponse: 'Humility, self-awareness, gradual softening',
        insight: 'Compassion is a muscle. The fact that it felt difficult for certain people is not failure — it is information about where your deepest work lies.',
        action: 'Write in your journal: "Compassion came most naturally toward ___. It was hardest to feel compassion for ___. This tells me ___."',
      },
      {
        title: 'Community Connection (Day 5)',
        icon: 'UsersIcon',
        reflection: ['Did I take any steps toward community this week?', 'How did it feel to consider sharing my journey with others?', 'What held me back, if anything?'],
        example: 'Taking the small but significant step of reading the community forum, even without posting — and feeling less alone',
        emotionalResponse: 'Vulnerability, hope, tentative belonging',
        insight: 'Even the smallest step toward community is significant. Isolation is one of the greatest barriers to healing. Every connection, however small, is a victory.',
        action: 'Write in your journal: "The step I took toward community this week was ___. What I felt was ___. Next week I will ___."',
      },
      {
        title: 'Forgiveness Letter Reflection (Day 6)',
        icon: 'LightBulbIcon',
        reflection: ['What did writing the forgiveness letter bring up for me?', 'Did anything shift after writing it?', 'Which letter was hardest to write, and why?'],
        example: 'Feeling unexpected grief while writing a letter to a parent — and then feeling lighter afterward, as if something had been released',
        emotionalResponse: 'Catharsis, grief, relief, empowerment',
        insight: 'The act of writing a forgiveness letter — even if never sent — begins to rewire the emotional narrative. The shift you felt is real. It is the beginning of freedom.',
        action: 'Write in your journal: "After writing my forgiveness letter, I felt ___. Something that shifted was ___. What I still need to work through is ___."',
      },
      {
        title: 'Week 1 Celebration & Recommitment',
        icon: 'TrophyIcon',
        reflection: ['What am I most proud of from this first week?', 'What was the hardest moment, and how did I get through it?', 'What do I want to carry into Week 2?'],
        example: 'Acknowledging that simply showing up for seven consecutive days of honest self-reflection is an act of extraordinary courage',
        emotionalResponse: 'Pride, gratitude, renewed determination',
        insight: 'You completed Week 1. That is not small. Most people never begin this work. You did. Celebrate that — and then take it into Week 2 with everything you have learned.',
        action: 'Write in your journal: "I am proud of myself for ___. My intention for Week 2 is ___. I recommit to this journey because ___." Then take one moment to celebrate yourself.',
      },
    ],
  },
};

// ─── Activities Data ──────────────────────────────────────────────────────────

const activities: DayActivity[] = [
  { day: 1, title: 'Emotional Baseline', category: 'Forgiveness Science', description: 'Identify your dominant emotions and their immediate triggers across five life areas: teamwork, romantic relationships, marriage, workplace conflict, and unemployment. Journal your emotional baseline to begin your self-awareness journey.', duration: '20 min' },
  { day: 2, title: 'Identifying Your Enemies', category: 'Healing Practices', description: 'Reflect on relationships where hostility exists. Write down names and situations that trigger negative emotions.', duration: '30 min' },
  { day: 3, title: 'The Cost of Hostility', category: 'Forgiveness Science', description: 'Examine how carrying grudges affects your mental health, relationships, and overall well-being.', duration: '25 min' },
  { day: 4, title: 'Compassion Meditation', category: 'Healing Practices', description: 'Guided meditation focusing on developing compassion for yourself and others, including those who have hurt you.', duration: '15 min' },
  { day: 5, title: 'Community Connection', category: 'Community Action', description: 'Join a local SIG Day chapter or online community. Share your journey and learn from others.', duration: '45 min' },
  { day: 6, title: 'Forgiveness Letter Writing', category: 'Healing Practices', description: 'Write a letter to someone who hurt you (not to send). Express your feelings and begin the release process.', duration: '40 min' },
  { day: 7, title: 'Weekly Reflection', category: 'Healing Practices', description: 'Review your first week. What emotions arose? What resistance did you feel? Celebrate small victories.', duration: '30 min' },
  { day: 8, title: 'The Neuroscience of Peace', category: 'Forgiveness Science', description: 'Learn how forgiveness rewires your brain and creates new neural pathways for compassion. Explore the stress-hostility loop, neuroplasticity, and the vagus nerve.', duration: '20 min' },
  { day: 9, title: 'Self-Forgiveness Practice', category: 'Healing Practices', description: 'Address guilt and shame you carry. Practice extending the same compassion to yourself that you offer others — forgiving past mistakes, emotional reactions, and your younger self.', duration: '35 min' },
  { day: 10, title: 'Cultural Perspectives on Forgiveness', category: 'Global Impact', description: 'Explore how Ubuntu, Ho\'oponopono, Buddhist Metta, Indigenous restorative justice, and Abrahamic traditions approach forgiveness and reconciliation.', duration: '25 min' },
  { day: 11, title: 'Boundary Setting', category: 'Healing Practices', description: 'Learn that forgiveness does not mean accepting harmful behavior. Establish healthy boundaries in family, workplace, romantic, and personal relationships.', duration: '30 min' },
  { day: 12, title: 'Community Service Day', category: 'Community Action', description: 'Engage in local humanitarian action. Experience the healing power of helping others — service as healing, serving through your gifts, and receiving gracefully.', duration: '2 hours' },
  { day: 13, title: 'Gratitude Practice', category: 'Healing Practices', description: 'Shift focus from what was taken to what remains. Cultivate gratitude for lessons through pain, people who stayed, your body, small moments, and the journey itself.', duration: '20 min' },
  { day: 14, title: 'Mid-Journey Reflection', category: 'Healing Practices', description: 'Assess your transformation at the halfway point. What has shifted? What remains difficult? Recommit to the second half of the journey.', duration: '45 min' },
  { day: 15, title: 'Conflict Resolution Skills', category: 'Community Action', description: 'Learn practical tools: active listening, Non-Violent Communication, de-escalation techniques, finding common ground, and repair after conflict.', duration: '40 min' },
  { day: 16, title: 'The Ripple Effect', category: 'Global Impact', description: 'Understand how your personal healing ripples into family systems, community, story, advocacy, and the global No Enemy vision.', duration: '25 min' },
  { day: 17, title: 'Visualization Exercise', category: 'Healing Practices', description: 'Guided visualization of living with no enemy — your body without resentment, relationships without enmity, community without division, and a world at peace.', duration: '20 min' },
  { day: 18, title: 'Sharing Your Story', category: 'Community Action', description: 'Record or write your transformation story — what brought you here, the hardest moment, what has shifted, and your invitation to others.', duration: '50 min' },
  { day: 19, title: 'Forgiveness Ceremony Preparation', category: 'Healing Practices', description: 'Plan your personal April 17th ceremony — choosing the form, what you are releasing, what you are committing to, who will witness, and connecting to the global ceremony.', duration: '35 min' },
  { day: 20, title: 'Research on Peace-Building', category: 'Forgiveness Science', description: 'Review the REACH model, forgiveness and physical health, mental health outcomes, relationship research, and evidence for collective healing.', duration: '30 min' },
  { day: 21, title: 'Third Week Reflection', category: 'Healing Practices', description: 'Notice how your perspective has shifted. Acknowledge collective healing progress, conflict resolution in practice, ripple effects, and prepare for Week 4.', duration: '30 min' },
  { day: 22, title: 'Organizing Local Celebration', category: 'Community Action', description: 'Coordinate with your community to plan April 17th activities — defining your event vision, inviting community, designing the experience, and sustaining beyond the day.', duration: '1 hour' },
  { day: 23, title: 'Advanced Meditation', category: 'Healing Practices', description: 'Deepen your practice with loving-kindness meditation for your most difficult person, systemic adversaries, yourself in difficulty, and all beings.', duration: '25 min' },
  { day: 24, title: 'Global Unity Preparation', category: 'Global Impact', description: 'Learn about celebrations happening worldwide. Connect with international participants, explore cross-cultural forgiveness, and craft your global message.', duration: '30 min' },
  { day: 25, title: 'Final Forgiveness Letters', category: 'Healing Practices', description: 'Complete remaining forgiveness work — the letter you have been avoiding, a letter to a system, a letter to the world, your final letter to yourself, and preparing for release.', duration: '45 min' },
  { day: 26, title: 'Commitment Ceremony Practice', category: 'Healing Practices', description: 'Rehearse your personal commitment to living with no enemy — defining your commitment, crafting your pledge statement, rehearsing the ceremony, and the long arc of commitment.', duration: '30 min' },
  { day: 27, title: 'Community Gathering', category: 'Community Action', description: 'Meet with your local chapter for final preparation. Share your journey, receive others\' stories, set collective intentions, and express gratitude for your community.', duration: '1.5 hours' },
  { day: 28, title: 'Final Week Reflection', category: 'Healing Practices', description: 'Review your entire 30-day journey — the transformation from Day 1 to Day 28, relationships that shifted, systemic change contributed, and what you carry forward.', duration: '40 min' },
  { day: 29, title: 'April 17th Preparation', category: 'Community Action', description: 'Finalize all logistics — ceremony checklist, community gathering confirmation, global participation plan, personal readiness, and a letter to tomorrow.', duration: '1 hour' },
  { day: 30, title: 'Eve of Transformation', category: 'Healing Practices', description: 'Final meditation and reflection — the journey complete, the person you have become, your April 17th intentions, your life beyond, and the final No Enemy meditation.', duration: '30 min' },
];

const weeksData = [
  { week: 1, label: 'Week 1', days: activities.slice(0, 7) },
  { week: 2, label: 'Week 2', days: activities.slice(7, 14) },
  { week: 3, label: 'Week 3', days: activities.slice(14, 21) },
  { week: 4, label: 'Week 4', days: activities.slice(21, 30) },
];

const getCategoryColor = (category: string): string => {
  const colors: Record<string, string> = {
    'Forgiveness Science': 'bg-healing-blue text-white',
    'Healing Practices': 'bg-peaceful-teal text-white',
    'Community Action': 'bg-transformation-gold text-accent-foreground',
    'Global Impact': 'bg-secondary text-white',
  };
  return colors[category] || 'bg-muted text-foreground';
};

// ─── Journal Modal ────────────────────────────────────────────────────────────

interface JournalModalProps {
  title: string;
  promptText: string;
  onClose: () => void;
  onSave: (entry: string, name: string) => Promise<void>;
  saving: boolean;
  saved: boolean;
}

function JournalModal({ title, promptText, onClose, onSave, saving, saved }: JournalModalProps) {
  const [entry, setEntry] = useState('');
  const [name, setName] = useState('');

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 flex flex-col max-h-[90vh]">
        <div className="bg-gradient-to-r from-primary to-secondary px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Icon name="PencilSquareIcon" size={18} className="text-white" />
            </div>
            <h3 className="text-white font-headline font-bold text-base">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <Icon name="XMarkIcon" size={16} className="text-white" />
          </button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="bg-primary/8 border border-primary/20 rounded-xl p-4">
            <p className="text-sm text-foreground font-body italic leading-relaxed whitespace-pre-line">{promptText}</p>
          </div>
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-1.5">
              Your Journal Entry <span className="text-red-500">*</span>
            </label>
            <textarea
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              rows={5}
              placeholder="Write your honest reflection here…"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none"
            />
          </div>
          <div>
            <label className="block text-sm font-cta font-bold text-foreground mb-1.5">
              Your Name <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Leave blank to submit as Anonymous"
              className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
            />
          </div>
          {saved && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <Icon name="CheckCircleIcon" size={18} className="text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700 font-body font-medium">
                Your response has been saved! Keep going — you are doing great.
              </p>
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button
              onClick={() => onSave(entry, name)}
              disabled={saving || !entry.trim()}
              className="flex-1 px-6 py-2.5 bg-primary text-primary-foreground rounded-xl font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting…</>
              ) : (
                <><Icon name="CheckIcon" size={16} className="text-primary-foreground" />Save Response</>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-5 py-2.5 border border-border text-foreground rounded-xl font-cta text-sm font-bold transition-all duration-300 hover:bg-muted"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Week1DayDetail Component ─────────────────────────────────────────────────

interface Week1DayDetailProps {
  day: number;
  activity: DayActivity;
  onClose: () => void;
}

const Week1DayDetail: React.FC<Week1DayDetailProps> = ({ day, activity, onClose }) => {
  const [activeSubmenu, setActiveSubmenu] = useState<'activities' | 'rituals'>('activities');
  const [openSection, setOpenSection] = useState<number | null>(null);
  const [expandedRitual, setExpandedRitual] = useState<number | null>(null);
  const content = week1DetailedContent[day];

  const [journalModal, setJournalModal] = useState<{
    open: boolean;
    title: string;
    promptText: string;
    responseType: 'scenario' | 'ritual';
    scenarioId?: number;
    scenarioContext?: string;
    ritualTime?: string;
  } | null>(null);
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalSaved, setJournalSaved] = useState(false);
  const [completedSections, setCompletedSections] = useState<Set<number>>(new Set());
  const [completedRituals, setCompletedRituals] = useState<Set<string>>(new Set());

  // Custom scenarios state
  const [customScenarios, setCustomScenarios] = useState<CustomScenario[]>([]);
  const [loadingCustom, setLoadingCustom] = useState(false);
  const [openCustomScenario, setOpenCustomScenario] = useState<string | null>(null);
  const [completedCustomScenarios, setCompletedCustomScenarios] = useState<Set<string>>(new Set());
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [form, setForm] = useState({
    context: '',
    reflection: '',
    example: '',
    emotional_response: '',
    insight: '',
    action: '',
    submitted_by: '',
  });

  useEffect(() => {
    if (activeSubmenu === 'activities' && day === 1) {
      setLoadingCustom(true);
      fetchCustomScenarios().then((data) => {
        setCustomScenarios(data);
        setLoadingCustom(false);
      });
    }
  }, [activeSubmenu, day]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAddScenario = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    const { data, error } = await insertCustomScenario({
      context: form.context.trim(),
      reflection: form.reflection.trim(),
      example: form.example.trim(),
      emotional_response: form.emotional_response.trim(),
      insight: form.insight.trim(),
      action: form.action.trim(),
      submitted_by: form.submitted_by.trim() || 'Anonymous',
    });
    setSubmitting(false);
    if (error) {
      setSubmitError(error);
    } else if (data) {
      setCustomScenarios((prev) => [data, ...prev]);
      setSubmitSuccess(true);
      setShowAddForm(false);
      setForm({ context: '', reflection: '', example: '', emotional_response: '', insight: '', action: '', submitted_by: '' });
      setTimeout(() => setSubmitSuccess(false), 4000);
    }
  };

  const handleSaveJournal = async (entry: string, name: string) => {
    if (!journalModal || !entry.trim()) return;
    setJournalSaving(true);
    try {
      await saveDailyResponse({
        participant_name: name || 'Anonymous',
        response_text: entry,
        response_type: journalModal.responseType,
        scenario_id: journalModal.scenarioId,
        scenario_context: journalModal.scenarioContext,
        ritual_time: journalModal.ritualTime,
        day_number: day,
      });
      setJournalSaved(true);
      if (journalModal.responseType === 'scenario' && journalModal.scenarioId !== undefined) {
        setCompletedSections((prev) => new Set([...prev, journalModal.scenarioId!]));
      } else if (journalModal.responseType === 'ritual' && journalModal.ritualTime) {
        setCompletedRituals((prev) => new Set([...prev, journalModal.ritualTime!]));
      } else if (journalModal.scenarioContext?.startsWith('custom:')) {
        const customId = journalModal.scenarioContext.replace('custom:', '');
        setCompletedCustomScenarios((prev) => new Set([...prev, customId]));
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setJournalSaving(false);
    }
  };

  if (!content) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      {journalModal?.open && (
        <JournalModal
          title={journalModal.title}
          promptText={journalModal.promptText}
          onClose={() => setJournalModal(null)}
          onSave={handleSaveJournal}
          saving={journalSaving}
          saved={journalSaved}
        />
      )}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">
        <div className="bg-primary/10 px-6 py-5 flex items-center justify-between border-b border-primary/20 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <span className="text-primary-foreground font-headline font-bold text-lg">{day}</span>
            </div>
            <div>
              <p className="text-xs font-cta font-bold text-primary uppercase tracking-widest mb-0.5">
                Week 1 · Day {day} · {activity.duration}
              </p>
              <h3 className="text-xl lg:text-2xl font-headline font-bold text-primary">
                {activity.title}
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors duration-200 flex-shrink-0"
            aria-label="Close"
          >
            <Icon name="XMarkIcon" size={18} className="text-primary" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          <div className="p-6 space-y-6">
            {/* Objective */}
            <div className="bg-white/60 rounded-xl p-5 border border-primary/10">
              <p className="text-sm font-cta font-bold text-primary uppercase tracking-wider mb-2">Objective</p>
              <p className="text-base text-foreground font-body leading-relaxed">{content.objective}</p>
            </div>

            {/* Submenu Tabs */}
            <div className="flex gap-2 border-b border-border">
              <button
                onClick={() => setActiveSubmenu('activities')}
                className={`px-5 py-2.5 rounded-t-lg font-cta text-sm font-bold transition-all duration-200 border-b-2 ${
                  activeSubmenu === 'activities' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                📋 Daily Activities
              </button>
              <button
                onClick={() => setActiveSubmenu('rituals')}
                className={`px-5 py-2.5 rounded-t-lg font-cta text-sm font-bold transition-all duration-200 border-b-2 ${
                  activeSubmenu === 'rituals' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                🕯️ Daily Ritual Practice
              </button>
            </div>

            {/* Daily Activities Tab */}
            {activeSubmenu === 'activities' && (
              <div className="space-y-3">
                <p className="text-sm font-cta font-bold text-muted-foreground uppercase tracking-wider">
                  {content.sections.length} Life Scenarios — Click to Explore
                </p>
                {content.sections.map((section, idx) => (
                  <div key={idx} className="rounded-xl border border-primary/15 overflow-hidden bg-white/50">
                    <button
                      onClick={() => setOpenSection(openSection === idx ? null : idx)}
                      className="w-full flex items-center justify-between px-5 py-4 hover:bg-primary/5 transition-colors duration-200 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name={section.icon as any} size={16} className="text-primary" />
                        </div>
                        <span className="font-cta font-bold text-foreground text-sm">{section.title}</span>
                        {completedSections.has(idx) && (
                          <Icon name="CheckCircleIcon" size={16} className="text-green-500" />
                        )}
                      </div>
                      <Icon
                        name={openSection === idx ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                        size={18}
                        className="text-primary flex-shrink-0"
                      />
                    </button>

                    {openSection === idx && (
                      <div className="px-5 pb-5 space-y-4 border-t border-primary/10">
                        <div className="pt-4">
                          <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-2">Reflection Questions</p>
                          <ul className="space-y-1.5">
                            {section.reflection.map((q, qi) => (
                              <li key={qi} className="flex items-start gap-2 text-sm text-foreground font-body">
                                <span className="text-primary mt-0.5 flex-shrink-0">→</span>
                                <span>{q}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-primary/5 rounded-lg p-4">
                          <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-1">Example</p>
                          <p className="text-sm text-foreground font-body italic">{section.example}</p>
                        </div>
                        <div>
                          <p className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-wider mb-1">Common Emotional Response</p>
                          <p className="text-sm text-foreground font-body">{section.emotionalResponse}</p>
                        </div>
                        <div className="bg-secondary/5 rounded-lg p-4">
                          <p className="text-xs font-cta font-bold text-secondary uppercase tracking-wider mb-1">Insight</p>
                          <p className="text-sm text-foreground font-body">{section.insight}</p>
                        </div>
                        <div className="bg-primary/5 rounded-lg p-4">
                          <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-1">✍ Journal Action</p>
                          <p className="text-sm text-foreground font-body italic">{section.action}</p>
                        </div>
                        <button
                          onClick={() => {
                            setJournalSaved(false);
                            setJournalModal({
                              open: true,
                              title: `Day ${day} · ${section.title}`,
                              promptText: section.action,
                              responseType: 'scenario',
                              scenarioId: idx,
                              scenarioContext: section.title,
                            });
                          }}
                          className={`w-full py-2.5 rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md flex items-center justify-center gap-2 ${
                            completedSections.has(idx)
                              ? 'bg-green-100 text-green-700 border border-green-300' :'bg-primary text-primary-foreground'
                          }`}
                        >
                          <Icon name={completedSections.has(idx) ? 'CheckCircleIcon' : 'PencilSquareIcon'} size={16} className={completedSections.has(idx) ? 'text-green-600' : 'text-primary-foreground'} />
                          {completedSections.has(idx) ? 'Response Saved ✓' : 'Record Response'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {/* How to Use Guide */}
                <div className="bg-white/60 rounded-xl p-4 border border-primary/10 flex gap-4">
                  <div className="w-10 h-10 bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="LightBulbIcon" size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-cta font-bold text-primary mb-1">How to Use This Guide</p>
                    <p className="text-sm text-foreground font-body leading-relaxed">{content.howToUse}</p>
                  </div>
                </div>

                {/* Community Scenarios */}
                {day === 1 && (
                  <>
                    <div className="mt-2">
                      <p className="text-sm font-cta font-bold text-muted-foreground uppercase tracking-wider mb-3">
                        Community Scenarios
                      </p>
                      {loadingCustom ? (
                        <div className="flex items-center gap-2 py-4">
                          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm text-muted-foreground font-body">Loading community scenarios…</span>
                        </div>
                      ) : customScenarios.length > 0 ? (
                        <div className="space-y-2">
                          {customScenarios.map((cs) => {
                            const isCustomDone = completedCustomScenarios.has(cs.id);
                            return (
                              <div key={cs.id} className="rounded-xl border border-secondary/20 overflow-hidden bg-white/50">
                                <button
                                  onClick={() => setOpenCustomScenario(openCustomScenario === cs.id ? null : cs.id)}
                                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-secondary/5 transition-colors duration-200 text-left"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                      <Icon name="UserGroupIcon" size={16} className="text-secondary" />
                                    </div>
                                    <span className="font-cta font-bold text-foreground text-sm">{cs.context}</span>
                                    {isCustomDone && <Icon name="CheckCircleIcon" size={16} className="text-green-500" />}
                                  </div>
                                  <Icon
                                    name={openCustomScenario === cs.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                                    size={18}
                                    className="text-secondary flex-shrink-0"
                                  />
                                </button>
                                {openCustomScenario === cs.id && (
                                  <div className="px-5 pb-5 space-y-3 border-t border-secondary/10">
                                    <div className="pt-3">
                                      <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-1">Reflection</p>
                                      <p className="text-sm text-foreground font-body">{cs.reflection}</p>
                                    </div>
                                    <div className="bg-primary/5 rounded-lg p-3">
                                      <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-1">Example</p>
                                      <p className="text-sm text-foreground font-body italic">{cs.example}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-wider mb-1">Emotional Response</p>
                                      <p className="text-sm text-foreground font-body">{cs.emotional_response}</p>
                                    </div>
                                    <div className="bg-secondary/5 rounded-lg p-3">
                                      <p className="text-xs font-cta font-bold text-secondary uppercase tracking-wider mb-1">Insight</p>
                                      <p className="text-sm text-foreground font-body">{cs.insight}</p>
                                    </div>
                                    <div className="bg-primary/5 rounded-lg p-3">
                                      <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-1">✍ Journal Action</p>
                                      <p className="text-sm text-foreground font-body italic">{cs.action}</p>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setJournalSaved(false);
                                        setJournalModal({
                                          open: true,
                                          title: `Community Scenario · ${cs.context}`,
                                          promptText: cs.action,
                                          responseType: 'scenario',
                                          scenarioId: undefined,
                                          scenarioContext: `custom:${cs.id}`,
                                        });
                                      }}
                                      className={`w-full py-2.5 rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md flex items-center justify-center gap-2 ${
                                        isCustomDone
                                          ? 'bg-green-100 text-green-700 border border-green-300' :'bg-primary text-primary-foreground'
                                      }`}
                                    >
                                      <Icon name={isCustomDone ? 'CheckCircleIcon' : 'PencilSquareIcon'} size={16} className={isCustomDone ? 'text-green-600' : 'text-primary-foreground'} />
                                      {isCustomDone ? 'Response Saved ✓' : 'Record Response'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>

                    {/* Add Your Own Scenario */}
                    <div className="mt-4 bg-gradient-to-br from-secondary/10 to-primary/10 rounded-2xl border-2 border-secondary/30 p-6">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <Icon name="PlusCircleIcon" size={22} className="text-secondary" />
                          </div>
                          <div>
                            <h4 className="text-base font-headline font-bold text-primary">Add Your Own Scenario</h4>
                            <p className="text-xs text-muted-foreground font-body">Share a life situation you face — it will be saved for the community.</p>
                          </div>
                        </div>
                        {!showAddForm && (
                          <button
                            onClick={() => setShowAddForm(true)}
                            className="flex-shrink-0 px-5 py-2 bg-secondary text-secondary-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md"
                          >
                            + Add Scenario
                          </button>
                        )}
                      </div>

                      {submitSuccess && (
                        <div className="mt-3 flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                          <Icon name="CheckCircleIcon" size={18} className="text-green-600 flex-shrink-0" />
                          <p className="text-sm text-green-700 font-body font-medium">Your scenario was added and is now visible to the community!</p>
                        </div>
                      )}

                      {showAddForm && (
                        <form onSubmit={handleAddScenario} className="mt-4 space-y-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="sm:col-span-2">
                              <label className="block text-xs font-cta font-bold text-foreground mb-1">Scenario Title / Context <span className="text-red-500">*</span></label>
                              <input type="text" name="context" value={form.context} onChange={handleFormChange} required placeholder='e.g. "Conflict with a close friend"' className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition" />
                            </div>
                            <div>
                              <label className="block text-xs font-cta font-bold text-foreground mb-1">Reflection <span className="text-red-500">*</span></label>
                              <textarea name="reflection" value={form.reflection} onChange={handleFormChange} required rows={2} placeholder="What do you reflect on in this situation?" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none" />
                            </div>
                            <div>
                              <label className="block text-xs font-cta font-bold text-foreground mb-1">Example <span className="text-red-500">*</span></label>
                              <textarea name="example" value={form.example} onChange={handleFormChange} required rows={2} placeholder="Give a brief real-life example" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none" />
                            </div>
                            <div>
                              <label className="block text-xs font-cta font-bold text-foreground mb-1">Emotional Response <span className="text-red-500">*</span></label>
                              <textarea name="emotional_response" value={form.emotional_response} onChange={handleFormChange} required rows={2} placeholder="How does this situation make you feel?" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none" />
                            </div>
                            <div>
                              <label className="block text-xs font-cta font-bold text-foreground mb-1">Insight <span className="text-red-500">*</span></label>
                              <textarea name="insight" value={form.insight} onChange={handleFormChange} required rows={2} placeholder="What insight or lesson does this bring?" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none" />
                            </div>
                            <div className="sm:col-span-2">
                              <label className="block text-xs font-cta font-bold text-foreground mb-1">Journal Action <span className="text-red-500">*</span></label>
                              <textarea name="action" value={form.action} onChange={handleFormChange} required rows={2} placeholder='"I feel… because I value…"' className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition resize-none" />
                            </div>
                            <div>
                              <label className="block text-xs font-cta font-bold text-foreground mb-1">Your Name <span className="text-muted-foreground font-normal">(optional)</span></label>
                              <input type="text" name="submitted_by" value={form.submitted_by} onChange={handleFormChange} placeholder="Leave blank to submit as Anonymous" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition" />
                            </div>
                          </div>
                          {submitError && (
                            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                              <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 flex-shrink-0" />
                              <p className="text-xs text-red-600 font-body">{submitError}</p>
                            </div>
                          )}
                          <div className="flex gap-3">
                            <button type="submit" disabled={submitting} className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                              {submitting ? (
                                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Submitting…</>
                              ) : (
                                <><Icon name="CheckIcon" size={16} className="text-primary-foreground" />Submit Scenario</>
                              )}
                            </button>
                            <button type="button" onClick={() => { setShowAddForm(false); setSubmitError(null); }} className="px-5 py-2.5 border border-border text-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:bg-muted">
                              Cancel
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Daily Ritual Practice Tab */}
            {activeSubmenu === 'rituals' && (
              <div className="space-y-4">
                {week1Rituals.map((ritual, idx) => (
                  <div key={ritual.time} className={`rounded-xl border ${ritual.borderColor} overflow-hidden`}>
                    <button
                      onClick={() => setExpandedRitual(expandedRitual === idx ? null : idx)}
                      className={`w-full flex items-center justify-between px-5 py-4 ${ritual.bgColor} hover:opacity-90 transition-opacity text-left`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xl">{ritual.emoji}</span>
                        </div>
                        <div>
                          <p className={`text-xs font-cta font-bold uppercase tracking-wider ${ritual.color}`}>{ritual.time} · {ritual.duration}</p>
                          <p className="font-headline font-bold text-foreground text-sm">{ritual.title}</p>
                        </div>
                        {completedRituals.has(ritual.time) && (
                          <Icon name="CheckCircleIcon" size={16} className="text-green-500" />
                        )}
                      </div>
                      <Icon
                        name={expandedRitual === idx ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                        size={18}
                        className={ritual.color}
                      />
                    </button>

                    {expandedRitual === idx && (
                      <div className="px-5 pb-5 space-y-4 bg-white/60">
                        <p className="text-sm text-foreground font-body leading-relaxed pt-4">{ritual.description}</p>
                        <div>
                          <p className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-wider mb-2">Steps</p>
                          <ol className="space-y-2">
                            {ritual.steps.map((step, si) => (
                              <li key={si} className="flex items-start gap-3 text-sm text-foreground font-body">
                                <span className={`w-6 h-6 rounded-full ${ritual.iconBg} flex items-center justify-center flex-shrink-0 text-xs font-bold ${ritual.color}`}>
                                  {si + 1}
                                </span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                        <button
                          onClick={() => {
                            setJournalSaved(false);
                            setJournalModal({
                              open: true,
                              title: `${ritual.emoji} ${ritual.time} Ritual · ${ritual.title}`,
                              promptText: `${ritual.description}\n\nSteps:\n${ritual.steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}`,
                              responseType: 'ritual',
                              ritualTime: ritual.time,
                            });
                          }}
                          className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md flex items-center justify-center gap-2"
                        >
                          <Icon name="PencilSquareIcon" size={16} className="text-primary-foreground" />
                          Record Response
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Week24DayDetail Component ────────────────────────────────────────────────

interface Week24DayDetailProps {
  selectedDay: number;
  dayData: import('./daysData').DayContent;
  activity: DayActivity;
  onClose: () => void;
}

const Week24DayDetail: React.FC<Week24DayDetailProps> = ({ selectedDay, dayData, activity, onClose }) => {
  const [activeSubmenu, setActiveSubmenu] = useState<'activities' | 'rituals'>('activities');
  const [openScenario, setOpenScenario] = useState<number | null>(null);
  const [expandedRitual, setExpandedRitual] = useState<number | null>(null);

  const [journalModal, setJournalModal] = useState<{
    open: boolean;
    title: string;
    promptText: string;
    responseType: 'scenario' | 'ritual';
    scenarioId?: number;
    scenarioContext?: string;
    ritualTime?: string;
  } | null>(null);
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalSaved, setJournalSaved] = useState(false);
  const [completedScenarios, setCompletedScenarios] = useState<Set<number>>(new Set());
  const [completedRituals, setCompletedRituals] = useState<Set<string>>(new Set());

  const handleSaveJournal = async (entry: string, name: string) => {
    if (!journalModal || !entry.trim()) return;
    setJournalSaving(true);
    try {
      await saveDailyResponse({
        participant_name: name || 'Anonymous',
        response_text: entry,
        response_type: journalModal.responseType,
        scenario_id: journalModal.scenarioId,
        scenario_context: journalModal.scenarioContext,
        ritual_time: journalModal.ritualTime,
        day_number: selectedDay,
      });
      setJournalSaved(true);
      if (journalModal.responseType === 'scenario' && journalModal.scenarioId !== undefined) {
        setCompletedScenarios((prev) => new Set([...prev, journalModal.scenarioId!]));
      } else if (journalModal.responseType === 'ritual' && journalModal.ritualTime) {
        setCompletedRituals((prev) => new Set([...prev, journalModal.ritualTime!]));
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setJournalSaving(false);
    }
  };

  const weekColors: Record<number, { badge: string; border: string; bg: string; text: string }> = {
    2: { badge: 'bg-rose-100 text-rose-700 border-rose-200', border: 'border-rose-200', bg: 'from-rose-50 to-pink-50', text: 'text-rose-700' },
    3: { badge: 'bg-teal-100 text-teal-700 border-teal-200', border: 'border-teal-200', bg: 'from-teal-50 to-emerald-50', text: 'text-teal-700' },
    4: { badge: 'bg-amber-100 text-amber-700 border-amber-200', border: 'border-amber-200', bg: 'from-amber-50 to-yellow-50', text: 'text-amber-700' },
  };
  const colors = weekColors[dayData.week] || weekColors[2];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      {journalModal?.open && (
        <JournalModal
          title={journalModal.title}
          promptText={journalModal.promptText}
          onClose={() => setJournalModal(null)}
          onSave={handleSaveJournal}
          saving={journalSaving}
          saved={journalSaved}
        />
      )}
      <div className="bg-card rounded-2xl w-full max-w-3xl flex flex-col max-h-[90vh] shadow-2xl border border-border">
        <div className="bg-primary/10 px-6 py-5 flex items-center justify-between border-b border-primary/20 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <span className="text-primary-foreground font-headline font-bold text-lg">{selectedDay}</span>
            </div>
            <div>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-sm font-cta font-bold border mb-1 ${colors.badge}`}>
                Week {dayData.week} · {dayData.weekTheme}
              </div>
              <h3 className="text-xl lg:text-2xl font-headline font-bold text-primary">
                {dayData.title}
              </h3>
              <p className="text-sm text-muted-foreground font-body">{dayData.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors duration-200 flex-shrink-0"
            aria-label="Close"
          >
            <Icon name="XMarkIcon" size={18} className="text-primary" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          <div className="p-6 space-y-6">
            {/* Objective */}
            <div className="bg-white/60 rounded-xl p-5 border border-primary/10">
              <p className="text-sm font-cta font-bold text-primary uppercase tracking-wider mb-2">Objective</p>
              <p className="text-base text-foreground font-body leading-relaxed">{dayData.objective}</p>
            </div>

            {/* Submenu Tabs */}
            <div className="flex gap-2 border-b border-border">
              <button
                onClick={() => setActiveSubmenu('activities')}
                className={`px-5 py-2.5 rounded-t-lg font-cta text-sm font-bold transition-all duration-200 border-b-2 ${
                  activeSubmenu === 'activities' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                📋 Daily Activities
              </button>
              <button
                onClick={() => setActiveSubmenu('rituals')}
                className={`px-5 py-2.5 rounded-t-lg font-cta text-sm font-bold transition-all duration-200 border-b-2 ${
                  activeSubmenu === 'rituals' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                🕯️ Daily Ritual Practice
              </button>
            </div>

            {/* Daily Activities Tab */}
            {activeSubmenu === 'activities' && (
              <div className="space-y-3">
                <p className="text-sm font-cta font-bold text-muted-foreground uppercase tracking-wider">
                  {dayData.scenarios.length} Life Scenarios — Click to Explore
                </p>
                {dayData.scenarios.map((scenario) => (
                  <div key={scenario.id} className="rounded-xl border border-primary/15 overflow-hidden bg-white/50">
                    <button
                      onClick={() => setOpenScenario(openScenario === scenario.id ? null : scenario.id)}
                      className="w-full flex items-center justify-between px-5 py-4 hover:bg-primary/5 transition-colors duration-200 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name={scenario.icon as any} size={16} className="text-primary" />
                        </div>
                        <span className="font-cta font-bold text-foreground text-sm">{scenario.context}</span>
                        {completedScenarios.has(scenario.id) && (
                          <Icon name="CheckCircleIcon" size={16} className="text-green-500" />
                        )}
                      </div>
                      <Icon
                        name={openScenario === scenario.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                        size={18}
                        className="text-primary flex-shrink-0"
                      />
                    </button>
                    {openScenario === scenario.id && (
                      <div className="px-5 pb-5 space-y-4 border-t border-primary/10">
                        <div className="pt-4">
                          <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-2">Reflection Questions</p>
                          <ul className="space-y-1.5">
                            {scenario.reflection.map((q, qi) => (
                              <li key={qi} className="flex items-start gap-2 text-sm text-foreground font-body">
                                <span className="text-primary mt-0.5 flex-shrink-0">→</span>
                                <span>{q}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-primary/5 rounded-lg p-4">
                          <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-1">Example</p>
                          <p className="text-sm text-foreground font-body italic">{scenario.example}</p>
                        </div>
                        <div>
                          <p className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-wider mb-1">Common Emotional Response</p>
                          <p className="text-sm text-foreground font-body">{scenario.emotionalResponse}</p>
                        </div>
                        <div className="bg-secondary/5 rounded-lg p-4">
                          <p className="text-xs font-cta font-bold text-secondary uppercase tracking-wider mb-1">Insight</p>
                          <p className="text-sm text-foreground font-body">{scenario.insight}</p>
                        </div>
                        <div className="bg-primary/5 rounded-lg p-4">
                          <p className="text-xs font-cta font-bold text-primary uppercase tracking-wider mb-1">✍ Journal Action</p>
                          <p className="text-sm text-foreground font-body italic">{scenario.action}</p>
                        </div>
                        <button
                          onClick={() => {
                            setJournalSaved(false);
                            setJournalModal({
                              open: true,
                              title: `Day ${selectedDay} · ${scenario.context}`,
                              promptText: scenario.action,
                              responseType: 'scenario',
                              scenarioId: scenario.id,
                              scenarioContext: scenario.context,
                            });
                          }}
                          className={`w-full py-2.5 rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md flex items-center justify-center gap-2 ${
                            completedScenarios.has(scenario.id)
                              ? 'bg-green-100 text-green-700 border border-green-300' :'bg-primary text-primary-foreground'
                          }`}
                        >
                          <Icon name={completedScenarios.has(scenario.id) ? 'CheckCircleIcon' : 'PencilSquareIcon'} size={16} className={completedScenarios.has(scenario.id) ? 'text-green-600' : 'text-primary-foreground'} />
                          {completedScenarios.has(scenario.id) ? 'Response Saved ✓' : 'Record Response'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Daily Ritual Practice Tab */}
            {activeSubmenu === 'rituals' && (
              <div className="space-y-4">
                {dayData.rituals.map((ritual, idx) => (
                  <div key={ritual.time} className={`rounded-xl border ${ritual.borderColor} overflow-hidden`}>
                    <button
                      onClick={() => setExpandedRitual(expandedRitual === idx ? null : idx)}
                      className={`w-full flex items-center justify-between px-5 py-4 ${ritual.bgColor} hover:opacity-90 transition-opacity text-left`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full ${ritual.iconBg} flex items-center justify-center flex-shrink-0`}>
                          <span className="text-xl">{ritual.emoji}</span>
                        </div>
                        <div>
                          <p className={`text-xs font-cta font-bold uppercase tracking-wider ${ritual.color}`}>{ritual.time} · {ritual.duration}</p>
                          <p className="font-headline font-bold text-foreground text-sm">{ritual.title}</p>
                        </div>
                        {completedRituals.has(ritual.time) && (
                          <Icon name="CheckCircleIcon" size={16} className="text-green-500" />
                        )}
                      </div>
                      <Icon
                        name={expandedRitual === idx ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                        size={18}
                        className={ritual.color}
                      />
                    </button>

                    {expandedRitual === idx && (
                      <div className="px-5 pb-5 space-y-4 bg-white/60">
                        <p className="text-sm text-foreground font-body leading-relaxed pt-4">{ritual.description}</p>
                        <div>
                          <p className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-wider mb-2">Steps</p>
                          <ol className="space-y-2">
                            {ritual.steps.map((step, si) => (
                              <li key={si} className="flex items-start gap-3 text-sm text-foreground font-body">
                                <span className={`w-6 h-6 rounded-full ${ritual.iconBg} flex items-center justify-center flex-shrink-0 text-xs font-bold ${ritual.color}`}>
                                  {si + 1}
                                </span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                        <button
                          onClick={() => {
                            setJournalSaved(false);
                            setJournalModal({
                              open: true,
                              title: `${ritual.emoji} ${ritual.time} Ritual · ${ritual.title}`,
                              promptText: `${ritual.description}\n\nSteps:\n${ritual.steps.map((s, i) => `${i + 1}. ${s}`).join('\n')}`,
                              responseType: 'ritual',
                              ritualTime: ritual.time,
                            });
                          }}
                          className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md flex items-center justify-center gap-2"
                        >
                          <Icon name="PencilSquareIcon" size={16} className="text-primary-foreground" />
                          Record Response
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── EAT Clock Component ──────────────────────────────────────────────────────

const EATClock: React.FC = () => {
  const [eatTime, setEATTime] = useState('');

  useEffect(() => {
    const tick = () => {
      setEATTime(formatEATTime(getEATNow()));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex items-center gap-2 bg-indigo-900/10 border border-indigo-200/50 rounded-lg px-4 py-2">
      <span className="text-lg">🕛</span>
      <div>
        <p className="text-xs font-cta font-bold text-muted-foreground uppercase tracking-wider leading-none">
          Dar es Salaam (EAT)
        </p>
        <p className="text-base font-headline font-bold text-indigo-700 tabular-nums">{eatTime}</p>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ThirtyDayCalendar = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [showStartForm, setShowStartForm] = useState(false);
  const [formEmail, setFormEmail] = useState('');
  const [formName, setFormName] = useState('');
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showNightMeditation, setShowNightMeditation] = useState(false);
  const [isMidnightWindow, setIsMidnightWindow] = useState(false);
  // unlockedUpToDay: how many days are currently unlocked based on EAT time
  const [unlockedUpToDay, setUnlockedUpToDay] = useState(1);

  // Compute unlocked days from journey start date
  const computeUnlockedDays = useCallback((startDateStr: string): number => {
    const eatNow = getEATNow();
    const todayStr = getEATDateString(eatNow);
    const start = new Date(startDateStr + 'T00:00:00Z');
    const today = new Date(todayStr + 'T00:00:00Z');
    const diff = Math.floor((today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return Math.min(diff + 1, 30);
  }, []);

  useEffect(() => {
    setIsHydrated(true);
    const savedEmail = localStorage.getItem('calendar_email');
    if (savedEmail) {
      loadProgress(savedEmail);
    }
    // Check midnight window
    setIsMidnightWindow(isEATMidnightWindow());
  }, []);

  // Tick to update unlocked days and midnight window
  useEffect(() => {
    if (!progress) return;
    const update = () => {
      setUnlockedUpToDay(computeUnlockedDays(progress.journeyStartDate));
      setIsMidnightWindow(isEATMidnightWindow());
    };
    update();
    // Re-check every minute
    const id = setInterval(update, 60 * 1000);
    return () => clearInterval(id);
  }, [progress, computeUnlockedDays]);

  // Schedule auto-refresh at next EAT midnight
  useEffect(() => {
    const ms = msUntilEATMidnight();
    const timeout = setTimeout(() => {
      if (progress) {
        setUnlockedUpToDay(computeUnlockedDays(progress.journeyStartDate));
      }
      setIsMidnightWindow(true);
    }, ms);
    return () => clearTimeout(timeout);
  }, [progress, computeUnlockedDays]);

  const loadProgress = useCallback(async (email: string) => {
    setIsLoading(true);
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('calendar_progress')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (error) {
        console.log('Load progress error:', error.message);
        return;
      }

      if (data) {
        const startDate = data.journey_start_date || getEATDateString();
        const prog: UserProgress = {
          email: data.email,
          fullName: data.full_name,
          completedDays: data.completed_days || [],
          currentDay: data.current_day || 1,
          journeyStartDate: startDate,
        };
        setProgress(prog);
        setUnlockedUpToDay(computeUnlockedDays(startDate));
      }
    } catch (err: any) {
      console.log('Load progress error:', err.message);
    } finally {
      setIsLoading(false);
    }
  }, [computeUnlockedDays]);

  const handleStartJourney = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!formEmail.trim() || !formName.trim()) {
      setFormError('Please enter your name and email.');
      return;
    }
    setIsLoading(true);
    try {
      const supabase = createClient();
      const { data: existing } = await supabase
        .from('calendar_progress')
        .select('*')
        .eq('email', formEmail.trim())
        .maybeSingle();

      if (existing) {
        const startDate = existing.journey_start_date || getEATDateString();
        localStorage.setItem('calendar_email', formEmail.trim());
        const prog: UserProgress = {
          email: existing.email,
          fullName: existing.full_name,
          completedDays: existing.completed_days || [],
          currentDay: existing.current_day || 1,
          journeyStartDate: startDate,
        };
        setProgress(prog);
        setUnlockedUpToDay(computeUnlockedDays(startDate));
        setShowStartForm(false);
        return;
      }

      const todayEAT = getEATDateString();
      const { data, error } = await supabase
        .from('calendar_progress')
        .insert({
          email: formEmail.trim(),
          full_name: formName.trim(),
          current_day: 1,
          completed_days: [],
          journey_start_date: todayEAT,
        })
        .select()
        .single();

      if (error) {
        console.log('Start journey error:', error.message);
        setFormError('Could not save your progress. Please try again.');
        return;
      }

      const startDate = data.journey_start_date || todayEAT;
      localStorage.setItem('calendar_email', formEmail.trim());
      const prog: UserProgress = {
        email: data.email,
        fullName: data.full_name,
        completedDays: data.completed_days || [],
        currentDay: data.current_day || 1,
        journeyStartDate: startDate,
      };
      setProgress(prog);
      setUnlockedUpToDay(computeUnlockedDays(startDate));
      setShowStartForm(false);
    } catch (err: any) {
      console.log('Start journey error:', err.message);
      setFormError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkComplete = async (day: number) => {
    if (!progress) return;
    if (progress.completedDays.includes(day)) return;

    setIsMarkingComplete(true);
    setSaveError('');
    try {
      const supabase = createClient();
      const newCompleted = [...progress.completedDays, day];
      const newCurrentDay = Math.min(day + 1, 30);

      const { error } = await supabase
        .from('calendar_progress')
        .update({
          completed_days: newCompleted,
          current_day: newCurrentDay,
          last_activity: new Date().toISOString(),
        })
        .eq('email', progress.email);

      if (error) {
        console.log('Mark complete error:', error.message);
        setSaveError('Could not save progress. Please try again.');
        return;
      }

      setProgress({
        ...progress,
        completedDays: newCompleted,
        currentDay: newCurrentDay,
      });
    } catch (err: any) {
      console.log('Mark complete error:', err.message);
      setSaveError('Could not save progress. Please try again.');
    } finally {
      setIsMarkingComplete(false);
    }
  };

  /**
   * Day status logic:
   * - completed: user has marked it done
   * - active: day is unlocked (≤ unlockedUpToDay) and not yet completed
   * - locked: day > unlockedUpToDay
   *
   * Without progress tracking: Day 1 is active, rest locked.
   */
  const getDayStatus = (day: number): 'completed' | 'active' | 'locked' => {
    if (!progress) {
      return day === 1 ? 'active' : 'locked';
    }
    if (progress.completedDays.includes(day)) return 'completed';
    if (day <= unlockedUpToDay) return 'active';
    return 'locked';
  };

  const selectedActivity = selectedDay ? activities.find(a => a.day === selectedDay) : null;
  const completedCount = progress?.completedDays?.length || 0;
  const progressPercent = Math.round((completedCount / 30) * 100);
  const isWeek1Day = selectedDay !== null && selectedDay >= 1 && selectedDay <= 7;
  const isDetailedDay = selectedDay !== null && selectedDay >= 1 && selectedDay <= 7;

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-headline font-bold text-primary mb-4">
              30-Day Transformation Calendar
            </h2>
            <p className="text-lg text-muted-foreground font-body">Loading your personalized journey...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Night Meditation Modal */}
        {showNightMeditation && (
          <NightMeditationModal onClose={() => setShowNightMeditation(false)} />
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-primary mb-4">
            30-Day Transformation Calendar
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Each day unlocks automatically at midnight <span className="font-bold text-indigo-700">Dar es Salaam time (EAT, UTC+3)</span>. Complete each day's practice and track your journey toward April 17th.
          </p>
        </div>

        {/* EAT Clock + Night Meditation Banner */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-10 bg-gradient-to-r from-indigo-900/5 to-purple-900/5 border border-indigo-200/50 rounded-xl p-4">
          <EATClock />

          {/* Night Meditation CTA */}
          <div className="flex items-center gap-3">
            {isMidnightWindow && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-cta font-bold animate-pulse">
                <span className="w-2 h-2 rounded-full bg-indigo-500 inline-block" />
                New Day Unlocked!
              </span>
            )}
            <button
              onClick={() => setShowNightMeditation(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-700 to-purple-700 text-white rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:opacity-90 hover:shadow-lg"
            >
              <span>🌙</span>
              Night Meditation
            </button>
          </div>
        </div>

        {/* Night Meditation Info Banner */}
        <div className="mb-8 bg-gradient-to-r from-indigo-900/8 to-purple-900/8 border border-indigo-200/60 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <span className="text-2xl">🌙</span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-cta font-bold text-indigo-800 mb-1">Night Meditation: No Enemy Living</p>
            <p className="text-sm text-muted-foreground font-body">
              An optional end-of-day ritual for the deeply committed. Each night at midnight (EAT), a new day unlocks and this meditation becomes available — guiding you to release the day's tensions and rest with no enemy in your heart.
            </p>
          </div>
          <button
            onClick={() => setShowNightMeditation(true)}
            className="flex-shrink-0 px-4 py-2 border border-indigo-300 text-indigo-700 rounded-lg font-cta text-xs font-bold hover:bg-indigo-50 transition-colors"
          >
            Begin Tonight
          </button>
        </div>

        {/* Progress Bar */}
        {progress && (
          <div className="bg-card rounded-xl p-6 mb-10 shadow-sm border border-border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <p className="text-lg font-headline font-bold text-primary">
                  Welcome back, {progress.fullName}
                </p>
                <p className="text-xs text-muted-foreground font-body mt-0.5">
                  Journey started: {progress.journeyStartDate} · Day {unlockedUpToDay} of 30 unlocked
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-headline font-bold text-success">{completedCount}/30</p>
                <p className="text-sm text-muted-foreground font-body">days completed</p>
              </div>
            </div>
            <div className="w-full bg-border rounded-full h-3">
              <div
                className="bg-success h-3 rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground font-body mt-2">{progressPercent}% complete — Keep going!</p>
          </div>
        )}

        {/* Start Journey CTA */}
        {!progress && !showStartForm && (
          <div className="text-center mb-10">
            <button
              onClick={() => setShowStartForm(true)}
              className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-cta text-base font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              Start Your Journey & Track Progress
            </button>
            <p className="text-sm text-muted-foreground font-body mt-3">
              Enter your email to save progress. Days unlock automatically each midnight (EAT).
            </p>
          </div>
        )}

        {/* Start Form */}
        {showStartForm && (
          <div className="bg-card rounded-xl p-8 mb-10 shadow-lg border border-border max-w-md mx-auto">
            <h3 className="text-xl font-headline font-bold text-primary mb-6 text-center">Begin Your 30-Day Journey</h3>
            <form onSubmit={handleStartJourney} className="space-y-4">
              <div>
                <label className="block text-sm font-cta font-bold text-foreground mb-2">Full Name</label>
                <input
                  type="text"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
                />
              </div>
              <div>
                <label className="block text-sm font-cta font-bold text-foreground mb-2">Email Address</label>
                <input
                  type="email"
                  value={formEmail}
                  onChange={e => setFormEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground font-body text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition"
                />
              </div>
              {formError && <p className="text-sm text-destructive font-body">{formError}</p>}
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : 'Start Journey'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowStartForm(false)}
                  className="px-6 py-3 bg-muted text-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:bg-card"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* All 4 Weeks */}
        <div className="space-y-10">
          {weeksData.map(({ week, label, days }) => {
            // Determine which days in this week are unlocked
            const unlockedDays = days.filter(activity => getDayStatus(activity.day) !== 'locked');
            // If no days in this week are unlocked, hide the entire week
            if (unlockedDays.length === 0) return null;

            return (
            <div key={week} className="bg-card rounded-xl p-4 sm:p-6 shadow-sm border border-border overflow-hidden">
              <div className="bg-primary/10 px-6 py-5 flex items-center justify-between border-b border-primary/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-foreground font-headline font-bold text-lg">{week}</span>
                  </div>
                  <div>
                    <p className="text-xs font-cta font-bold text-primary uppercase tracking-widest mb-0.5">
                      {label}
                    </p>
                    <p className="text-sm text-muted-foreground font-body">
                      Days {days[0].day}–{days[days.length - 1].day}
                    </p>
                  </div>
                  {week === 1 && (
                    <span className="ml-auto text-xs font-cta font-bold text-primary bg-primary/10 px-3 py-1 rounded-full border border-primary/20">
                      Click any unlocked day to explore full guide
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-4 xs:grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 gap-2 sm:gap-3">
                {days.map((activity) => {
                  const status = getDayStatus(activity.day);
                  const isCompleted = status === 'completed';
                  const isActive = status === 'active';
                  const isLocked = status === 'locked';
                  const isSelected = selectedDay === activity.day;

                  // Hide locked days completely
                  if (isLocked) return null;

                  return (
                    <button
                      key={activity.day}
                      onClick={() => {
                        setSelectedDay(isSelected ? null : activity.day);
                      }}
                      title={activity.title}
                      className={`relative p-2 sm:p-3 rounded-lg transition-all duration-300 text-center ${
                        isCompleted
                          ? 'bg-success/10 border-2 border-success cursor-pointer hover:shadow-md'
                          : isActive
                          ? 'bg-primary/10 border-2 border-primary cursor-pointer hover:shadow-md hover:bg-primary/20'
                          : 'bg-muted border-2 border-transparent opacity-40 cursor-not-allowed'
                      } ${isSelected ? 'ring-2 ring-offset-2 ring-primary shadow-lg scale-105' : ''}`}
                    >
                      <div className={`text-base sm:text-xl font-headline font-bold mb-1 sm:mb-1 ${
                        isCompleted ? 'text-success' : isActive ? 'text-primary' : 'text-muted-foreground'
                      }`}>
                        {activity.day}
                      </div>
                      {isCompleted ? (
                        <Icon name="CheckCircleIcon" size={16} className="text-success mx-auto" />
                      ) : isActive ? (
                        <Icon name="PlayCircleIcon" size={16} className="text-primary mx-auto" />
                      ) : (
                        <Icon name="LockClosedIcon" size={16} className="text-muted-foreground mx-auto" />
                      )}
                      <p className="text-xs text-muted-foreground font-body mt-1 leading-tight line-clamp-2 hidden sm:block">
                        {activity.title}
                      </p>
                      {isActive && isSelected && (
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-primary rotate-45" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Week 1 Expanded Day Content */}
              {week === 1 && isDetailedDay && selectedDay !== null && getDayStatus(selectedDay) !== 'locked' && (
                <Week1DayDetail
                  day={selectedDay}
                  activity={activities.find(a => a.day === selectedDay)!}
                  onClose={() => setSelectedDay(null)}
                />
              )}

              {/* Weeks 2-4 Expanded Day Content */}
              {week !== 1 && selectedDay !== null && days.some(d => d.day === selectedDay) && getDayStatus(selectedDay) !== 'locked' && (() => {
                const dayData = allDaysContent[selectedDay];
                if (!dayData) return null;
                return (
                  <Week24DayDetail
                    key={selectedDay}
                    selectedDay={selectedDay}
                    dayData={dayData}
                    activity={activities.find(a => a.day === selectedDay)!}
                    onClose={() => setSelectedDay(null)}
                  />
                );
              })()}

            </div>
            );
          })}
        </div>

        {/* Unlock Info Banner */}
        <div className="mt-8 bg-indigo-50 border border-indigo-200 rounded-xl p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <Icon name="ClockIcon" size={20} className="text-indigo-600" />
          </div>
          <div>
            <p className="text-sm font-cta font-bold text-indigo-800 mb-1">How Day Unlocking Works</p>
            <p className="text-sm text-indigo-700 font-body leading-relaxed">
              All 30 days are prepared and ready. Each day unlocks automatically at <strong>midnight Dar es Salaam time (EAT, UTC+3)</strong>. Day 1 is available immediately when you start your journey. Day 2 unlocks the following midnight, Day 3 the midnight after, and so on — keeping you on a steady, intentional pace of transformation.
            </p>
          </div>
        </div>

        {/* Activity Detail Card for non-Week-1 days */}
        {selectedActivity && !isWeek1Day && getDayStatus(selectedActivity.day) !== 'locked' && (
          <div className="mt-10 bg-card rounded-xl p-8 shadow-lg border border-border animate-fade-in">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <div className={`px-4 py-1 rounded-full text-sm font-cta font-bold ${getCategoryColor(selectedActivity.category)}`}>
                    {selectedActivity.category}
                  </div>
                  <span className="text-sm text-muted-foreground font-body">
                    Day {selectedActivity.day} • {selectedActivity.duration}
                  </span>
                  {getDayStatus(selectedActivity.day) === 'completed' && (
                    <span className="flex items-center gap-1 px-3 py-1 bg-success/10 text-success rounded-full text-sm font-cta font-bold">
                      <Icon name="CheckCircleIcon" size={16} className="text-success" />
                      Completed
                    </span>
                  )}
                </div>
                <h3 className="text-2xl lg:text-3xl font-headline font-bold text-primary mb-4">
                  {selectedActivity.title}
                </h3>
                <p className="text-base text-foreground font-body leading-relaxed mb-6">
                  {selectedActivity.description}
                </p>

                {saveError && (
                  <p className="text-sm text-destructive font-body mb-4">{saveError}</p>
                )}

                <div className="flex flex-col sm:flex-row gap-4">
                  {getDayStatus(selectedActivity.day) === 'active' && progress && (
                    <button
                      onClick={() => handleMarkComplete(selectedActivity.day)}
                      disabled={isMarkingComplete}
                      className="px-6 py-3 bg-success text-white rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Icon name="CheckCircleIcon" size={18} className="text-white" />
                      {isMarkingComplete ? 'Saving...' : 'Mark as Complete'}
                    </button>
                  )}
                  {getDayStatus(selectedActivity.day) === 'active' && !progress && (
                    <button
                      onClick={() => setShowStartForm(true)}
                      className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md"
                    >
                      Track Your Progress
                    </button>
                  )}
                  {getDayStatus(selectedActivity.day) === 'completed' && (
                    <div className="flex items-center gap-2 px-6 py-3 bg-success/10 text-success rounded-lg font-cta text-sm font-bold">
                      <Icon name="CheckCircleIcon" size={18} className="text-success" />
                      Day Completed!
                    </div>
                  )}
                  <button className="px-6 py-3 bg-muted text-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:bg-card">
                    Download Resources
                  </button>
                </div>
              </div>

              <div className="lg:w-64">
                <div className="bg-muted rounded-lg p-6">
                  <h4 className="text-lg font-headline font-bold text-primary mb-4">Your Progress</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm font-body">
                      <span className="text-muted-foreground">Completed</span>
                      <span className="text-primary font-bold">{completedCount}/30 days</span>
                    </div>
                    <div className="w-full bg-border rounded-full h-2">
                      <div className="bg-success h-2 rounded-full transition-all duration-500" style={{ width: `${progressPercent}%` }} />
                    </div>
                    <p className="text-xs text-muted-foreground font-body">
                      {completedCount === 0
                        ? 'Start Day 1 to begin your transformation journey!'
                        : completedCount === 30
                        ? '🎉 Congratulations! You completed all 30 days!'
                        : `Keep going! Day ${completedCount + 1} awaits you.`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ThirtyDayCalendar;